import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldTableColumn, TableRow, TableData } from '../../../models/truth-management.model';
import { NotificationService } from '../../../services/notification.service';
import { TruthManagement as TruthManagementService } from '../../../services/truth-management';

/**
 * Shared component for displaying and editing table field data
 * Works with dictionary-based table storage:
 * {"ColumnName": "val1,val2,val3", "Col2": "a,b,c"} (one entry per table)
 */
@Component({
  selector: 'app-table-row-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './table-row-input.component.html',
  styleUrl: './table-row-input.component.scss'
})
export class TableRowInputComponent implements OnInit, OnChanges {
  @Input() columns: FieldTableColumn[] = [];
  @Input() tableData: TableData = {};  // Dictionary format: {"ColumnName": ["val1", "val2", "val3"], ...}
  @Input() attributeId: number | string | null = null;
  @Input() isReadOnly: boolean = false;
  @Input() isExtracting: boolean = false;
  @Input() isAutoPopulated: boolean = false;
  @Input() isOverridden: boolean = false;
  @Input() hasAutoFilledBoundingBox: boolean = false;
  @Input() cellBboxKeys: Set<string> = new Set();
  @Input() canSnip: boolean = false;
  @Input() pendingSnipIndices: Set<number> = new Set();

  @Output() tableDataChanged = new EventEmitter<TableData>();  // Emit dictionary format for saving
  @Output() rowAdded = new EventEmitter<void>();
  @Output() rowRemoved = new EventEmitter<number>();
  @Output() rowEdited = new EventEmitter<{ rowIndex: number; columnName: string; value: string }>();
  @Output() cellEditingChanged = new EventEmitter<boolean>();  // Fired when a cell enters/exits edit mode, so the parent can show "unsaved changes" immediately
  @Output() cellClicked = new EventEmitter<{ rowIndex: number; columnName: string }>();  // Fired when a cell is clicked, so the parent can highlight only that cell's bounding box
  @Output() snipToggleClicked = new EventEmitter<{ rowIndex: number; columnName: string; event: MouseEvent }>();
  @Output() tableReverted = new EventEmitter<void>();

  // Parsed rows for display
  rows: TableRow[] = [];

  private cellOriginals = new Map<string, string>();
  private isOwnEmit = false;

  editingRowIndex: number | null = null;
  editingColumnName: string | null = null;
  editingValue: string = '';
  originalCellValue: string = '';  // Track original value before editing to detect actual changes
  activatingRowColumn: { rowIndex: number; columnName: string } | null = null;

  isPickingDate: boolean = false;  // Suppresses blur validation while the native date picker is being used
  private isRefocusingAfterError: boolean = false;  // Suppresses blur validation while refocusing after an invalid-date alert

  @ViewChild('activeCellInput') set activeCellInput(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) {
      ref.nativeElement.focus();
      ref.nativeElement.select();
    }
  }

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private notificationService: NotificationService,
    private truthManagementService: TruthManagementService
  ) {}

  rowHasAnyBbox(rowIndex: number): boolean {
    for (const key of this.cellBboxKeys) {
      if (key.startsWith(`${rowIndex}:`)) return true;
    }
    return false;
  }

  getCellBboxNumber(rowIndex: number, columnName: string): number {
    let num = 0;
    for (let r = 0; r < this.rows.length; r++) {
      for (const col of this.columns) {
        if (this.cellBboxKeys.has(`${r}:${col.name}`)) {
          num++;
          if (r === rowIndex && col.name === columnName) return num;
        }
      }
    }
    return num;
  }

  getCellFlatIndex(rowIndex: number, columnName: string): number {
    const colIndex = this.columns.findIndex(c => c.name === columnName);
    return rowIndex * this.columns.length + (colIndex >= 0 ? colIndex : 0);
  }

  hasPendingSnip(rowIndex: number, columnName: string): boolean {
    return this.pendingSnipIndices.has(this.getCellFlatIndex(rowIndex, columnName));
  }

  ngOnInit(): void {
    this.parseTableData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData'] && !changes['tableData'].firstChange) {
      this.parseTableData();
      if (!this.isOwnEmit) {
        this.cellOriginals.clear();
      }
      this.isOwnEmit = false;
    }
  }

  /**
   * Parse dictionary table data into display rows
   * Format: {"ColumnName": ["val1", "val2", "val3"], "Col2": ["a", "b", "c"]} =>
   *   [{ColumnName: "val1", Col2: "a"}, {ColumnName: "val2", Col2: "b"}, ...]
   */
  private parseTableData(): void {
    const columnNames = this.columns.map(col => col.name);
    this.rows = this.parseTableRowsFromDictionary(this.tableData, columnNames);
  }

  /**
   * Parse table dictionary data into structured TableRow array
   */
  private parseTableRowsFromDictionary(tableData: TableData, columnNames: string[]): TableRow[] {
    if (!tableData || !columnNames || columnNames.length === 0 || Object.keys(tableData).length === 0) {
      return [];
    }

    const rowCount = this.getTableRowCount(tableData, columnNames);
    if (rowCount === 0) {
      return [];
    }

    const rows: TableRow[] = [];

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      const row: TableRow = {};
      
      columnNames.forEach(columnName => {
        const columnValues = tableData[columnName] || [];
        row[columnName] = columnValues[rowIndex] || null;
      });
      
      rows.push(row);
    }

    return rows;
  }

  /**
   * Determine the number of rows by checking any column's array length
   */
  private getTableRowCount(tableData: TableData, columnNames: string[]): number {
    for (const columnName of columnNames) {
      const value = tableData[columnName];
      if (Array.isArray(value) && value.length > 0) {
        return value.length;
      }
    }
    return 0;
  }

  /**
   * Detect a date column by name, matching the attribute-level date detection used
   * for non-table fields (attributeLabel/attributeKey containing "date")
   */
  isDateColumn(columnName: string): boolean {
    return this.truthManagementService.isDateFieldName(columnName);
  }

  /**
   * Handle a data cell click: notify the parent to highlight only this cell's bounding box,
   * then enter edit mode. Stops propagation so the attribute-level click handler (which
   * highlights the whole field) doesn't also fire.
   */
  onCellClick(rowIndex: number, columnName: string, event: MouseEvent): void {
    event.stopPropagation();
    this.cellClicked.emit({ rowIndex, columnName });
    this.startEditCell(rowIndex, columnName);
  }

  /**
   * Start editing a cell
   */
  startEditCell(rowIndex: number, columnName: string): void {
    if (this.isReadOnly || this.isExtracting) return;

    // Already editing this cell (e.g. clicking inside the input to reposition the cursor) -
    // don't reset editingValue and wipe out the in-progress edit
    if (this.editingRowIndex === rowIndex && this.editingColumnName === columnName) {
      return;
    }

    this.editingRowIndex = rowIndex;
    this.editingColumnName = columnName;
    this.editingValue = this.rows[rowIndex]?.[columnName] || '';
    this.originalCellValue = this.editingValue;  // Store original for change detection
    this.activatingRowColumn = null; // Clear activating state
    this.cellEditingChanged.emit(true);
  }

  /**
   * Save cell edit - serialize back to dictionary format and emit
   */
  saveCell(): void {
    if (this.editingRowIndex !== null && this.editingColumnName !== null) {
      this.isPickingDate = false;
      this.isRefocusingAfterError = false;

      const rowIndex = this.editingRowIndex;
      const columnName = this.editingColumnName;
      const value = this.editingValue;

      const key = `${rowIndex}:${columnName}`;
      if (!this.cellOriginals.has(key)) {
        this.cellOriginals.set(key, this.originalCellValue);
      }

      // Update row data
      if (!this.rows[rowIndex]) {
        this.rows[rowIndex] = {};
      }
      this.rows[rowIndex][columnName] = value;

      // Serialize rows back to dictionary format and emit
      const columnNames = this.columns.map(col => col.name);
      const updatedTableData = this.serializeTableRowsToDictionary(this.rows, columnNames);

      // Emit events
      this.rowEdited.emit({ rowIndex, columnName, value });
      this.isOwnEmit = true;
      this.tableDataChanged.emit(updatedTableData);

      // Clear editing state
      this.editingRowIndex = null;
      this.editingColumnName = null;
      this.editingValue = '';
      this.originalCellValue = '';
      this.cellEditingChanged.emit(false);
    }
  }

  /**
   * Handle a cell input losing focus.
   * The parent listens for (tableDataChanged)/(cellEditingChanged) and calls
   * cdr.detectChanges() synchronously, which can destroy the still-focused input
   * mid-navigation (e.g. during Tab/Enter) and fire a delayed/reentrant blur for it
   * after editingRowIndex/editingColumnName already moved to the next cell. Only save
   * if this is still the actively-edited cell, so that stale blur is a no-op.
   * 
   * Implements same pattern as non-table fields: if user clicked without actually
   * changing the value, remove "Save Changes" indicator by not emitting tableDataChanged.
   */
  onCellBlur(rowIndex: number, columnName: string): void {
    if (this.editingRowIndex === rowIndex && this.editingColumnName === columnName) {
      // Skip validation if user is clicking the date picker
      if (this.isPickingDate) {
        this.isPickingDate = false;
        return;
      }
      // Skip validation if refocusing after an invalid-date alert
      if (this.isRefocusingAfterError) {
        this.isRefocusingAfterError = false;
        return;
      }

      if (this.isDateColumn(columnName) && this.editingValue.trim()) {
        const normalized = this.truthManagementService.normalizeDateToMMDDYYYY(this.editingValue.trim());
        if (!this.truthManagementService.isValidDateFormat(normalized)) {
          this.showInvalidDateAlert(normalized);
          return;
        }
        this.editingValue = normalized;
      }

      // Check if value actually changed (matching non-table field pattern).
      // Date columns compare normalized values so equivalent formats (e.g. "1/2/2024" vs "01/02/2024") aren't treated as edits.
      const currentValue = this.editingValue.trim();
      const originalValue = this.originalCellValue.trim();
      const valuesEqual = this.isDateColumn(columnName)
        ? this.truthManagementService.normalizeDateForComparison(currentValue) === this.truthManagementService.normalizeDateForComparison(originalValue)
        : currentValue === originalValue;

      if (valuesEqual) {
        // No-op: user clicked but didn't edit - just exit edit mode without marking as modified
        this.editingRowIndex = null;
        this.editingColumnName = null;
        this.editingValue = '';
        this.originalCellValue = '';
        this.cellEditingChanged.emit(false);
        return;
      }
      this.saveCell();
    }
  }

  /**
   * Commit whatever cell is currently being edited, if any
   * Called by the parent before saving, since a mid-edit cell hasn't blurred yet
   */
  commitActiveEdit(): void {
    if (this.editingRowIndex !== null && this.editingColumnName !== null) {
      const columnName = this.editingColumnName;

      // Validate date columns before committing, matching saveAllChanges' toaster on invalid date
      if (this.isDateColumn(columnName) && this.editingValue.trim()) {
        const normalized = this.truthManagementService.normalizeDateToMMDDYYYY(this.editingValue.trim());
        this.editingValue = normalized;
        if (!this.truthManagementService.isValidDateFormat(normalized)) {
          this.notificationService.showSaveError(this.truthManagementService.getDateValidationErrorMessage(normalized));
          this.refocusActiveCellInput();
          return;
        }
      }

      this.saveCell();
    }
  }

  /**
   * Refocus the still-editing cell's input after a validation error is shown,
   * so the user can continue correcting the value without re-clicking the cell.
   */
  private refocusActiveCellInput(): void {
    this.isRefocusingAfterError = true;
    setTimeout(() => {
      const input = this.elRef.nativeElement.querySelector('.table-cell.editing .cell-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 0);
  }

  /**
   * Cancel cell edit (restore original value)
   */
  cancelEdit(): void {
    this.editingRowIndex = null;
    this.editingColumnName = null;
    this.editingValue = '';
    this.originalCellValue = '';
    this.cellEditingChanged.emit(false);
  }

  /**
   * Serialize TableRow array back to dictionary format
   * [{ColumnName: "val1", Col2: "a"}, {ColumnName: "val2", Col2: "b"}] =>
   *   {"ColumnName": ["val1", "val2"], "Col2": ["a", "b"]}
   */
  private serializeTableRowsToDictionary(rows: TableRow[], columnNames: string[]): TableData {
    if (!rows || rows.length === 0 || !columnNames || columnNames.length === 0) {
      return {};
    }

    const tableData: TableData = {};
    
    columnNames.forEach(columnName => {
      const columnValues = rows.map(row => row[columnName] || '');
      tableData[columnName] = columnValues;
    });

    return tableData;
  }

  /**
   * Add a new empty row
   */
  addNewRow(): void {
    if (this.isReadOnly || this.isExtracting) return;

    const newRow: TableRow = {};
    this.columns.forEach(col => {
      newRow[col.name] = null;
    });

    this.rows.push(newRow);

    // Serialize and emit updated table data
    const columnNames = this.columns.map(col => col.name);
    const updatedTableData = this.serializeTableRowsToDictionary(this.rows, columnNames);
    this.isOwnEmit = true;
    this.cellOriginals.clear();
    this.tableDataChanged.emit(updatedTableData);
    this.rowAdded.emit();
  }

  /**
   * Remove a row at the given index
   */
  removeRow(rowIndex: number): void {
    if (this.isReadOnly || this.isExtracting) return;

    this.rows.splice(rowIndex, 1);

    if (this.editingRowIndex === rowIndex) {
      this.cancelEdit();
    } else if (this.editingRowIndex !== null && this.editingRowIndex > rowIndex) {
      this.editingRowIndex--;
    }

    // Serialize and emit updated table data
    const columnNames = this.columns.map(col => col.name);
    const updatedTableData = this.serializeTableRowsToDictionary(this.rows, columnNames);
    this.isOwnEmit = true;
    this.cellOriginals.clear();
    this.tableDataChanged.emit(updatedTableData);
    this.rowRemoved.emit(rowIndex);
  }

  /**
   * Get cell value or empty string
   */
  getCellValue(rowIndex: number, columnName: string): string {
    return this.rows[rowIndex]?.[columnName] || '';
  }

  /**
   * Check if cell is currently being edited
   */
  isCellEditing(rowIndex: number, columnName: string): boolean {
    return this.editingRowIndex === rowIndex && this.editingColumnName === columnName;
  }

  isCellModified(rowIndex: number, columnName: string): boolean {
    return this.cellOriginals.has(`${rowIndex}:${columnName}`);
  }

  undoCellEdit(rowIndex: number, columnName: string, event: MouseEvent): void {
    event.stopPropagation();
    const key = `${rowIndex}:${columnName}`;
    const originalValue = this.cellOriginals.get(key);
    if (originalValue === undefined) return;

    this.rows[rowIndex][columnName] = originalValue;
    this.cellOriginals.delete(key);

    const columnNames = this.columns.map(col => col.name);
    const updatedTableData = this.serializeTableRowsToDictionary(this.rows, columnNames);

    if (this.cellOriginals.size === 0) {
      this.tableReverted.emit();
    } else {
      this.isOwnEmit = true;
      this.tableDataChanged.emit(updatedTableData);
    }
  }

  /**
   * Tooltip shown on a cell in display mode, matching the "Click to edit" pattern
   * used for single-value fields in truth-management.html
   */
  getCellTooltip(rowIndex: number, columnName: string): string {
    if (this.isExtracting) {
      return 'Auto-filling in progress...';
    }
    const hasValue = !!this.getCellValue(rowIndex, columnName).trim();
    if (this.isDateColumn(columnName)) {
      return hasValue ? 'Click to edit date' : 'Click to add date';
    }
    return hasValue ? 'Click to edit' : 'Click to add value';
  }

  /**
   * Track for *ngFor optimization
   */
  trackByRowIndex(index: number): number {
    return index;
  }

  trackByColumnName(column: FieldTableColumn): string {
    return column.name;
  }

  /**
   * Handle keyboard navigation in cell edit, matching Excel:
   * Tab steps forward through columns in the same row (horizontal);
   * Enter (Shift+Enter) steps through rows in the same column (vertical).
   * Reaching the end of one axis wraps into the next position on the other axis.
   */
  onCellKeydown(event: KeyboardEvent, rowIndex: number, columnName: string): void {
    if (this.isDateColumn(columnName)) {
      this.truthManagementService.restrictDateKeydown(event);
      if (event.defaultPrevented) {
        return;
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.navigateCell(rowIndex, columnName, 'row', event.shiftKey ? -1 : 1);
    } else if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      this.navigateCell(rowIndex, columnName, 'column', 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  /**
   * Save the current cell and move edit focus along the given axis.
   * 'row' steps through rows in the same column, wrapping into the next/previous column.
   * 'column' steps through columns in the same row, wrapping into the next/previous row.
   */
  private navigateCell(rowIndex: number, columnName: string, axis: 'row' | 'column', direction: 1 | -1): void {
    this.saveCell();

    const columnNames = this.columns.map(col => col.name);
    let targetRow = rowIndex;
    let targetCol = columnNames.indexOf(columnName);

    if (axis === 'row') {
      targetRow += direction;
      if (targetRow < 0) {
        targetRow = this.rows.length - 1;
        targetCol -= 1;
      } else if (targetRow >= this.rows.length) {
        targetRow = 0;
        targetCol += 1;
      }
    } else {
      targetCol += direction;
      if (targetCol < 0) {
        targetCol = columnNames.length - 1;
        targetRow -= 1;
      } else if (targetCol >= columnNames.length) {
        targetCol = 0;
        targetRow += 1;
      }
    }

    if (targetRow < 0 || targetRow >= this.rows.length || targetCol < 0 || targetCol >= columnNames.length) {
      return;
    }

    this.startEditCell(targetRow, columnNames[targetCol]);
  }

  /**
   * Set activating state for immediate visual feedback
   */
  setActivatingState(rowIndex: number, columnName: string): void {
    this.activatingRowColumn = { rowIndex, columnName };
  }

  /**
   * Clear activating state
   */
  clearActivatingState(): void {
    this.activatingRowColumn = null;
  }

  // ---- Date column support - delegates to TruthManagementService, shared with the
  // single-value date field pattern in truth-management.ts, so this logic lives in
  // exactly one place. ----

  /**
   * Auto-format digits as MM/DD/YYYY while the user types in a date cell
   */
  onDateCellInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.truthManagementService.formatDigitsToMMDDYYYY(input.value);

    this.editingValue = formatted;
    if (input.value !== formatted) {
      input.value = formatted;
    }

    const cursorPos = formatted.length;
    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }

  /**
   * Convert the cell's current MM/DD/YYYY value to yyyy-mm-dd for the native date picker
   */
  getDatePickerValue(): string {
    return this.truthManagementService.getDatePickerValueFromText(this.editingValue);
  }

  /**
   * Handle a date picked from the native date picker
   */
  onDatePickerChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (!value) return;

    this.editingValue = this.truthManagementService.formatDateForDisplay(value);
    this.saveCell();
  }

  /**
   * Suppress blur validation before the native date picker opens (mousedown fires before blur)
   */
  onDatePickerIconMousedown(event: Event): void {
    event.preventDefault();
    this.isPickingDate = true;
  }

  /**
   * Open the hidden native date picker for the active cell
   */
  openDatePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isPickingDate = true;

    const icon = event.target as HTMLElement;
    const wrapper = icon.closest('.date-input-wrapper');
    const datePicker = wrapper?.querySelector('.date-picker-hidden') as HTMLInputElement | null;
    if (datePicker) {
      setTimeout(() => {
        datePicker.focus();
        try {
          datePicker.showPicker();
        } catch (e) {
          datePicker.click();
        }
      }, 0);
    }
  }

  /**
   * Show a specific validation message for an invalid MM/DD/YYYY date, then refocus the cell input
   */
  private showInvalidDateAlert(normalized: string): void {
    alert(this.truthManagementService.getDateValidationErrorMessage(normalized));
    this.refocusActiveCellInput();
  }
}
